import { EosApi, EosUtils, IFindTableParams } from "@pro/common/eos";
import { TransactResult } from "eosjs/dist/eosjs-api-interfaces";
import { Authorization } from "eosjs/dist/eosjs-serialize";
import { AAsset, ACollection, ASchema, ATemplate, TActionName, TActionParam, TUint64 } from "./aa_types";

export class AAContract
{
	private readonly _api: EosApi;
	private readonly _account: string;

	constructor(api: EosApi, account: string)
	{
		this._account = account;
		this._api = api;
	}

	action<N extends TActionName>(name: N,
	                              auth: Authorization | Authorization[],
	                              data: TActionParam<N>): Promise<TransactResult>
	{
		return this._api.transact([{
			name: name,
			account: this._account,
			authorization: EosUtils.castAuth(auth),
			data: data,
		}]);
	}

	findCollection(name: string)
	{
		return this._api.findRecord<ACollection>(name, {
			code: this._account,
			scope: this._account,
			table: "collections",
			key_type: "name",
		});
	}

	findSchema(collection: string, schemaName: string)
	{
		return this._api.findRecord<ASchema>(schemaName, {
			code: this._account,
			scope: collection,
			table: "schemas",
			key_type: "name",
		});
	}

	findTemplate(collection: string, templateId: number): Promise<ATemplate | undefined>
	{
		return this._api.findRecord<ATemplate>(templateId, {
			code: this._account,
			scope: collection,
			table: "templates",
			key_type: "i64",
		});
	}

	async findLastTemplate(collection: string): Promise<ATemplate | undefined>
	{
		let response = await this._api.getTable<ATemplate>({
			code: this._account,
			scope: collection,
			table: "templates",
			key_type: "i64",
			reverse: true,
			limit: 1,
		});
		return response.rows[0];
	}

	findAsset(owner: string, assetId: TUint64)
	{
		return this._api.findRecord<AAsset>(assetId, {
			code: this._account,
			scope: owner,
			table: "assets",
			key_type: "i64",
		});
	}

	getAsset(owner: string, assetId: TUint64)
	{
		return this._api.getRecord<AAsset>(assetId, {
			code: this._account,
			scope: owner,
			table: "assets",
			key_type: "i64",
		});
	}

	getAllSchemas(collection: string,
	              params?: Pick<IFindTableParams, "lower_bound">): Promise<ASchema[]>
	{
		return this._api.getFullTable({
			code: this._account,
			scope: collection,
			table: "schemas",
			lower_bound: params?.lower_bound,
			key_type: "name",
		});
	}


	getAllTemplates(collection: string): Promise<ATemplate[]>
	{
		return this._api.getFullTable({
			code: this._account,
			scope: collection,
			table: "templates",
			key_type: "i64",
		});
	}

	getAllAssets(account: string,
	             params?: Pick<IFindTableParams, "lower_bound">): Promise<AAsset[]>
	{
		return this._api.getFullTable({
			code: this._account,
			scope: account,
			table: "assets",
			lower_bound: params?.lower_bound,
			key_type: "i64",
		});
	}

	async findLastAsset(account: string): Promise<AAsset | undefined>
	{
		let result = await this.api.getTable<AAsset>({
			code: this._account,
			scope: account,
			table: "assets",
			key_type: "i64",
			reverse: true,
			limit: 1,
		});
		return result.rows[0];
	}

	async getLastAsset(account: string): Promise<AAsset>
	{
		let asset = await this.findLastAsset(account);
		if (!asset)
			throw new Error(`no records found for scope: ${account}`);
		return asset;
	}

	get active(): Authorization
	{
		return {actor: this._account, permission: "active"};
	}

	get account()
	{
		return this._account;
	}

	get api()
	{
		return this._api;
	}
}
